import React, { useEffect, useState } from "react";
import {
  EditOutlined,
  SaveOutlined,
  PlusOutlined,
  DownloadOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { List, Upload, Spin, Col, Input, Tooltip, Row, Modal } from "antd";
import { get, indexOf, isEmpty, replace, toLower } from "lodash";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Button from "../../../../../components/functional_components/Button/PFButton";
import {
  buildQueryString,
  checkRoles,
  exportFile,
  showError,
  showSuccess,
  trimData,
} from "../../../../../utils/utilities";
import {
  permittedFileFormatUpload,
  permittedFileFormatUploadName,
  fileFormatXextension,
  projectsManagementTabs,
  dbDate,
  langMonth,
} from "../../../../../utils/properties";
import { setLoading } from "../../../../../redux/ducks/Loading";
import { useFormik } from "formik";
import Project from "../../../../../services/api/Project";
import ProjectDocument from "../../../../../services/api/ProjectDocument";
import dayjs from "dayjs";
const { Dragger } = Upload;
const { confirm } = Modal;

const DocumentsArchive = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langMonth[toLower(currentLanguage)];
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const projectManagement = useSelector((state) => get(state.ProjectManagementDuck, "project", {}));
  const openPlanning = useSelector((state) => get(state.ProjectManagementDuck, "openPlanning", {}));
  const closed = useSelector((state) => get(state.ProjectManagementDuck, "closed", {}));
  const [state, setState] = useState({
    documentsList: [],
    loading: false,
    documentModal: {
      visible: false,
      type: "",
      data: {},
    },
    isEdit: false,
    fileList: [],
  });

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      isEdit: checkRoles(userInfo.roles, projectsManagementTabs["ProjectForm"].edit),
    }));
    getDocumentArchive();
  }, [projectManagement]);

  const getDocumentArchive = async () => {
    dispatch(setLoading(true));
    const projectDocumentsList = await Project.getDocumentList(params.id);
    if (!projectDocumentsList.error && projectDocumentsList.data) {
      setState((prevState) => ({
        ...prevState,
        documentsList: get(projectDocumentsList, "data", []),
      }));
    } else {
      showError(get(projectDocumentsList, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const handleDownload = (doc) => async () => {
    const downloadType = fileFormatXextension[replace(doc.fileExtension, ".", "")];
    dispatch(setLoading(true));
    const file = await ProjectDocument.downloadDocumentContractualRevenue(doc.id);
    if (!file.error) {
      const blob = new Blob([file], { type: downloadType });
      exportFile(blob, `${doc.description}.${doc.fileExtension}`);
      showSuccess();
    } else {
      showError(get(file, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const deleteCallback = (id) => () => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        dispatch(setLoading(true));
        const deleteResponse = await ProjectDocument.deleteDocumentContractualRevenue(id);
        if (!deleteResponse.error) {
          showSuccess();
          getDocumentArchive();
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
        dispatch(setLoading(false));
      },
      onCancel() {},
    });
  };

  const openDocumentModal =
    (item = {}) =>
    () => {
      if (isEmpty(item)) {
        setState((prevState) => ({ ...prevState, documentModal: { visible: true, type: "new", data: {} } }));
      } else {
        setState((prevState) => ({ ...prevState, documentModal: { visible: true, type: "edit", data: item } }));
      }
    };

  const closeModal = () => {
    setState((prevState) => ({
      ...prevState,
      documentModal: { visible: false, type: "", data: {} },
      fileList: [],
    }));
  };

  const formikDocument = useFormik({
    initialValues: {
      description: get(state.documentModal, "data.description", ""),
      file: "",
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!(values.description && /^[a-zA-Z0-9àéèìòù_-\s]{1,50}$/.test(values.description))) {
        errors.description = "Required!";
      }
      if (state.documentModal.type === "new") {
        if (!values.file) {
          // file is required only on new, in edit I cannot upload file and edit only description
          errors.file = "Required!";
        }
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      let queryObj = {
        description: values.description,
      };
      const formData = new FormData();
      if (isEmpty(state.fileList)) {
        const fakeFile = new File([], "fakefile.txt");
        formData.append("file", fakeFile);
      } else {
        state.fileList.forEach((file) => {
          formData.append("file", file);
        });
      }
      if (state.documentModal.type === "edit") {
        queryObj.documentId = state.documentModal.data.id;
      }
      const queryString = buildQueryString({}, queryObj);
      setState((prevState) => ({ ...prevState, loading: true }));
      const response = await Project.saveProjectDocument(params.id, queryString.string, formData);
      if (!response.error) {
        showSuccess();
        closeModal();
        getDocumentArchive();
        formikDocument.resetForm();
      } else {
        showError(get(response, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false, fileList: [] }));
    },
  });

  const props = {
    multiple: false,
    onRemove: (file) => {
      const index = state.fileList.indexOf(file);
      const newFileList = state.fileList.slice();
      newFileList.splice(index, 1);
      setState((prevState) => ({
        ...prevState,
        fileList: newFileList,
      }));
      formikDocument.setFieldValue("file", "");
    },
    beforeUpload: (file) => {
      let ckFileList = [];
      let ckFileValue = "";
      if (indexOf(permittedFileFormatUpload, file.type) !== -1) {
        ((ckFileList = [file]), (ckFileValue = "ok"));
      } else {
        showError(t("projectEstimate.modal.errorFileFormat"));
      }
      setState((prevState) => ({
        ...prevState,
        fileList: ckFileList,
      }));
      formikDocument.setFieldValue("file", ckFileValue);
      return false;
    },
    fileList: state.fileList,
  };

  const checkDisabledButton = () => {
    let returnValue = false;
    if (userInfo.canEditProjectForm && projectManagement.enabled) {
      // If I can edit and the project is enabled, I check the month.
      if (!(closed && openPlanning)) {
        // If the month has been closed but the variable "openPlanning" is true then the button is enabled. If the month is open or closed but the variable "openPlanning" is false then I check if the month is approved.
        returnValue = projectManagement.approved; // If the month has been approved the button is disabled else is enabled se il mese è stato approvato il bottone è disabilitato, altrimenti è abilitato.
      }
    } else {
      returnValue = true;
    }
    return returnValue;
  };

  return (
    <>
      <Row justify="end" className="mb-1em">
        <Button
          isDisabled={checkDisabledButton()}
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={openDocumentModal()}
        />
      </Row>
      <List
        header={<strong>{t("projectEstimate.detail.tabs.preventiveForm.titleDocument")}</strong>}
        dataSource={state.documentsList}
        size="small"
        renderItem={(item) => (
          <List.Item
            actions={[
              <div className={"list-actions"} key={item.id} onClick={handleDownload(item)}>
                <DownloadOutlined className={`icon-small`} />
              </div>,
              <div
                className={`list-actions ${
                  !(state.isEdit && projectManagement.enabled && item.editable) && "disabled notAllowedCursor"
                }`}
                key={item.id}
                onClick={
                  state.isEdit && projectManagement.enabled && item.editable ? openDocumentModal(item) : undefined
                }
              >
                <EditOutlined className="icon-small" />
              </div>,
              <div
                className={`list-actions ${
                  !(state.isEdit && projectManagement.enabled && item.editable) && "disabled notAllowedCursor"
                }`}
                key={item.id}
                onClick={
                  state.isEdit && projectManagement.enabled && item.editable ? deleteCallback(item.id) : undefined
                }
              >
                <DeleteOutlined className="icon-small" />
              </div>,
            ]}
          >
            <List.Item.Meta
              title={<div>{`${dayjs(item.uploadDate, dbDate).format(dateFormat)} | ${item.description}`}</div>}
            />
          </List.Item>
        )}
      />

      <Modal
        width={700}
        title={
          <b>
            {state.documentModal.type === "new"
              ? t("projectEstimate.detail.tabs.preventiveForm.titleNewDocument")
              : t("projectEstimate.detail.tabs.preventiveForm.titleEditDocument")}
          </b>
        }
        open={state.documentModal.visible}
        onCancel={closeModal}
        footer={[
          <Button
            isLoading={state.loading}
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikDocument.submitForm}
            isDisabled={!(formikDocument.dirty && formikDocument.isValid)}
          />,
        ]}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 48]}>
          <Col span={24}>
            <Row>
              <Col span={12}>
                <label className="fw-bold" htmlFor="description">
                  {t("projectEstimate.modal.description")}*
                </label>
              </Col>
              <Col span={12} className="right">
                <Tooltip
                  title={
                    <div>
                      <p>{t("projectEstimate.modal.infoTooltip.maxLenght")}</p>
                      <p>{t("projectEstimate.modal.infoTooltip.specialChar")}</p>
                    </div>
                  }
                >
                  <InfoCircleOutlined className="icon-small" />
                </Tooltip>
              </Col>
            </Row>
            <Row>
              <Input
                className={formikDocument.errors.description !== undefined && "input-error"}
                required
                id="description"
                name="description"
                showCount
                maxLength={50}
                onChange={formikDocument.handleChange}
                onBlur={formikDocument.handleBlur}
                value={formikDocument.values.description}
              />
            </Row>
            <Row type="flex" justify="center" align="center" className="mt-1em mb-10">
              <Col span={24}>
                <p>
                  {t("projectEstimate.modal.allowedExtensions")}{" "}
                  {permittedFileFormatUploadName.map((ext, i) =>
                    i < permittedFileFormatUploadName.length - 1 ? `${ext}, ` : ext
                  )}
                </p>
              </Col>
              <Col span={24}>
                <Dragger {...props}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    {state.documentModal.type === "new"
                      ? t("projectEstimate.detail.tabs.preventiveForm.uploadText")
                      : t("projectEstimate.detail.tabs.preventiveForm.uploadTextEdit")}
                  </p>
                </Dragger>
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};
export default DocumentsArchive;
